/**
 * DEV-1110 — encrypted-at-rest storage for the device credential.
 *
 * AES-256-GCM in `localStorage`, with the key in IndexedDB — the same shape as
 * the WMS mobile PWA (`wms/crypto.js`: db `MetashipSecure`, store `keys`, id
 * `device-key`), so a device that later holds both credentials holds them the
 * same way.
 *
 * ── Two deliberate differences from the WMS reference ────────────────────────
 *
 * 1. **The key is non-extractable.** WMS generates an extractable key and
 *    stores its raw bytes (`exportKey('raw')`). IndexedDB can structured-clone
 *    a `CryptoKey` itself, so the material never has to exist as bytes any
 *    script can read. Script on this origin can still ASK the key to decrypt —
 *    that is unavoidable for a browser app — but it can no longer copy the key
 *    out and use it elsewhere, which is the difference between an XSS that can
 *    act while it runs and one that walks away with the secret.
 *
 * 2. **There is no plaintext fallback.** WMS falls back to
 *    `JSON.stringify(storage)` when its crypto helper is missing. Here, a
 *    device whose browser cannot encrypt simply does not get enrolled: it keeps
 *    working online exactly as it does today. A 30-day credential sitting in
 *    readable localStorage is worse than no offline mode.
 */

const DB_NAME = "MetashipSecure";
const STORE_NAME = "keys";
const KEY_ID = "device-key";
const ALGORITHM = "AES-GCM";
const KEY_LENGTH = 256;
/** AES-GCM's standard 96-bit nonce. */
const IV_BYTES = 12;

/**
 * Thrown when this browser cannot store a secret safely (no WebCrypto, no
 * IndexedDB, or both blocked — a private window, or an insecure context).
 * Callers treat it as "this device cannot be enrolled", never as an error to
 * show a grower standing in an orchard.
 */
export class SecureStoreUnavailableError extends Error {
  constructor(message = "This browser cannot store a device credential securely") {
    super(message);
    this.name = "SecureStoreUnavailableError";
  }
}

function subtleCrypto(): SubtleCrypto {
  const subtle = typeof crypto !== "undefined" ? crypto.subtle : undefined;
  if (!subtle) throw new SecureStoreUnavailableError();
  return subtle;
}

function openKeyStore(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new SecureStoreUnavailableError());
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error ?? new SecureStoreUnavailableError());
    // Firefox in private mode resolves `open` and then never fires either
    // handler on the transaction; `onblocked` at least surfaces the common
    // "another tab holds an older version" case instead of hanging the boot.
    request.onblocked = () => reject(new SecureStoreUnavailableError("Key store is blocked by another tab"));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
  });
}

function readKey(db: IDBDatabase): Promise<CryptoKey | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(KEY_ID);
    request.onsuccess = () => {
      const row = request.result as { key?: CryptoKey } | undefined;
      resolve(row?.key instanceof CryptoKey ? row.key : null);
    };
    request.onerror = () => reject(request.error);
  });
}

function writeKey(db: IDBDatabase, key: CryptoKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const request = tx.objectStore(STORE_NAME).put({ id: KEY_ID, key, createdAt: Date.now() });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/** The device key, generated on first use. One key per browser profile. */
async function getOrCreateKey(): Promise<CryptoKey> {
  const subtle = subtleCrypto();
  const db = await openKeyStore();
  try {
    const existing = await readKey(db);
    if (existing) return existing;
    const key = await subtle.generateKey({ name: ALGORITHM, length: KEY_LENGTH }, false, [
      "encrypt",
      "decrypt",
    ]);
    await writeKey(db, key);
    return key;
  } finally {
    db.close();
  }
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

/** Encrypt a JSON-serialisable value to `base64(iv ‖ ciphertext)`. */
export async function encryptJson(value: unknown): Promise<string> {
  const key = await getOrCreateKey();
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await subtleCrypto().encrypt({ name: ALGORITHM, iv }, key, plaintext);
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return toBase64(combined);
}

/**
 * Decrypt what `encryptJson` wrote, or `null` for anything that does not come
 * back cleanly — a corrupted blob, or one written under a key this profile no
 * longer has. Null means "no credential", which lands the caller on the normal
 * online sign-in rather than on an error.
 */
export async function decryptJson<T>(encoded: string): Promise<T | null> {
  try {
    const key = await getOrCreateKey();
    const combined = fromBase64(encoded);
    const iv = combined.slice(0, IV_BYTES);
    const ciphertext = combined.slice(IV_BYTES);
    const plaintext = await subtleCrypto().decrypt({ name: ALGORITHM, iv }, key, ciphertext);
    return JSON.parse(new TextDecoder().decode(plaintext)) as T;
  } catch {
    return null;
  }
}

/** Can this browser hold a device credential at all? */
export function isSecureStoreAvailable(): boolean {
  return (
    typeof crypto !== "undefined" &&
    !!crypto.subtle &&
    typeof indexedDB !== "undefined" &&
    typeof localStorage !== "undefined"
  );
}

/**
 * Forget the device key. Called on an explicit sign-out, alongside deleting the
 * encrypted credential: without this the blob would be gone but its key would
 * outlive it, which is just litter with a security smell.
 */
export async function destroyKey(): Promise<void> {
  try {
    const db = await openKeyStore();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const request = tx.objectStore(STORE_NAME).delete(KEY_ID);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } finally {
      db.close();
    }
  } catch {
    // Best-effort: a key with nothing left to decrypt is inert.
  }
}
